import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NetworkRequest, Organization } from "../../../../../../common/models";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorOutcomeOption } from "../../../../../../common/service/server/Types";
import { useAuthorizations } from "../../../../../hooks/useAuthorizations";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { Routes } from "../../../../../root/routes";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Abbr } from "../../../../Abbr";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { SurveyAnswers, useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  DataAccessibilityIntegrationAvailability,
  getRequestProcessingRegion,
  isRequestContextApplicableWithCrunchFiltering,
  isVendorDataAccessibleForWorkflow,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";

import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { getVendorNesting } from "../../../../../util/vendorNesting";
import { VendorDeleteRow } from "../shared/VendorDeleteRow";
import { RetainedVendorRow } from "../shared/RetainedVendorRow";
import { AUTOMATION_OUTCOMES, AutomationOutcomeRow } from "../shared/AutomationOutcomeRow";
import {
  isAwaitingConnectedSystemAutomation,
  useConnectedSystemAutomationBlock,
} from "../shared/ConnectedSystemAutomationCallout";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { CategoryHeader } from "../shared/CategoryHeader";
import { useInstructionsState } from "../shared/useInstructionsState";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { DataMapDto } from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationManagedAdNetworkDto } from "../../../../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { useAvailableIntegrationVendorIds } from "../../../../../hooks/useAvailableIntegrationVendorIds";

export type VendorOutcomeHandler = (
  vendor: OrganizationDataRecipientDto,
  outcome: VendorOutcomeOption,
) => void;

export type VendorOutcomeCollection = Record<string, string>;

/**
 * Whether a recipient belongs in the Delete Info list on its own merits, ignoring vendor nesting. It is also part of
 * the test for listing a recipient under its manager here - see `getDeleteInfoStepNesting`.
 */
const belongsInDeleteInfoStep = (
  vendor: OrganizationDataRecipientDto,
  request: DataSubjectRequestDetailDto, // if null, will skip request-specific filters
  dataMap: DataMapDto,
  organization: Organization,
  apiIntegrations: DataAccessibilityIntegrationAvailability,
): boolean => {
  // First do checks that are request-specific
  if (request) {
    const requestProcessingRegion = getRequestProcessingRegion(request);

    if (!vendor.processingRegions.includes(requestProcessingRegion)) {
      return false;
    }

    const contextOk = isRequestContextApplicableWithCrunchFiltering(
      dataMap,
      request.context,
      vendor,
      request,
      organization,
    );

    if (!contextOk) {
      return false;
    }
  }

  // Skip internal storage locations
  if (vendor.dataRecipientType === "storage_location" && vendor.isCustom) {
    return false;
  }

  const instruction = vendor.instructions.find((vi) => vi.requestType == "DELETE");
  const instructionOk = instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED";
  const isDataDeletable = vendor.dataDeletability !== "NOT_DELETABLE";
  return (
    instructionOk && isDataDeletable && isVendorDataAccessibleForWorkflow(vendor, apiIntegrations)
  );
};

export const getDeleteInfoStepNesting = (
  allVendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto,
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  organization: Organization,
  apiIntegrations: DataAccessibilityIntegrationAvailability,
) =>
  getVendorNesting(allVendors, managedAdNetworks, sellingAndSharingAnswers, {
    // Everything a manager handles is listed under it here, as long as this step would have shown the recipient at
    // all and it is actually deleted from - a retained recipient keeps its place in the Data Retention section.
    isNestedVendorRelevant: (vendor) =>
      belongsInDeleteInfoStep(vendor, request, dataMap, organization, apiIntegrations) &&
      !vendor.isInstalled &&
      canVendorHaveDataDeleted(vendor),
  });

export const vendorsForDeleteInfoStep = (
  allVendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto, // if null, will skip request-specific filters
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  organization: Organization,
  apiIntegrations: DataAccessibilityIntegrationAvailability,
): OrganizationDataRecipientDto[] => {
  const nesting = getDeleteInfoStepNesting(
    allVendors,
    request,
    dataMap,
    sellingAndSharingAnswers,
    managedAdNetworks,
    organization,
    apiIntegrations,
  );

  const result = allVendors.filter(
    (vendor) =>
      // Vendors nested under a manager are handled there, so they are not listed separately
      // (similar to DeleteInstructionsPage and OptOutInstructionsPage)
      !nesting.isManaged(vendor.id) &&
      belongsInDeleteInfoStep(vendor, request, dataMap, organization, apiIntegrations),
  );

  // Add management vendors that should appear in deletion workflows (similar to opt-out workflow). `activeManagers`
  // only holds managers that manage at least one recipient shown in this step.
  const managementVendorsToAdd = nesting.activeManagers.filter(
    (vendor) =>
      !result.find((v) => v.id === vendor.id) &&
      isVendorDataAccessibleForWorkflow(vendor, apiIntegrations),
  );

  return result.concat(managementVendorsToAdd);
};

export const canVendorHaveDataDeleted = (vendor: OrganizationDataRecipientDto): boolean => {
  // A DELETE instruction with `processingMethod="RETAIN"` and `hasRetentionExceptions=false` means that either the data must be retained or the data cannot be deleted, and therefore should *not* be included in the list of delete vendors (i.e. this excludes vendors whose corresponding data deletability in the Data Storage settings table is either "No - data must be retained" or "No - unable to delete data")
  return !vendor.instructions.some(
    (i) =>
      i.requestType == "DELETE" && i.processingMethod === "RETAIN" && !i.hasRetentionExceptions,
  );
};

export const DeleteInfo = () => {
  const [org, orgRequest] = usePrimaryOrganization();
  const [allVendors, vendorsRequest] = useOrganizationVendors(org.id, undefined, true);
  const { request, setStepCompleted, mutations } = useDataRequestProcessingContext();
  const [readyToRender, setReadyToRender] = useState(false);
  const { data: dataMap } = useDataMap(org.id);
  const instructionsState = useInstructionsState();

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);
  const { integrationAvailability, privacyCenterRequest, integrationAvailabilityRequest } =
    useAvailableIntegrationVendorIds();

  const { authorizations, request: authorizationsRequest } = useAuthorizations(org.id);

  const onSelectionChange: VendorOutcomeHandler = (vendor, value) => {
    mutations.updateVendorOutcomeMutation.mutate({ vendor: vendor.vendorId, outcome: value });
  };

  // Effects
  useEffect(() => {
    if (
      NetworkRequest.areFinished(
        vendorsRequest,
        orgRequest,
        authorizationsRequest,
        privacyCenterRequest,
        integrationAvailabilityRequest,
      )
    ) {
      setReadyToRender(true);
    }
  }, [
    orgRequest,
    vendorsRequest,
    authorizationsRequest,
    privacyCenterRequest,
    integrationAvailabilityRequest,
  ]);

  const { activeManagers: managementVendors, getManagedVendorsForManager } =
    getDeleteInfoStepNesting(
      allVendors,
      request,
      dataMap,
      answers,
      managedAdNetworks,
      org,
      integrationAvailability,
    );

  const vendors = vendorsForDeleteInfoStep(
    allVendors,
    request,
    dataMap,
    answers,
    managedAdNetworks,
    org,
    integrationAvailability,
  );

  const installedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors?.filter((v) => !v.isInstalled) ?? []);
  }, [vendors]);

  const deleteVendors = useMemo(() => {
    return installedVendors.filter(canVendorHaveDataDeleted);
  }, [installedVendors]);

  const retainVendors = useMemo(() => {
    return installedVendors.filter((v) =>
      v.instructions.some((i) => i.requestType == "DELETE" && i.processingMethod === "RETAIN"),
    );
  }, [installedVendors]);

  // Vendors that will show up in both deleteVendors and retainVendors: DELETE instruction with `processingMethod="RETAIN"` and `hasRetentionExceptions=true` (i.e. the data is partially deleted)

  useEffect(() => {
    const outcomes = request.vendorOutcomes;
    const everyOutcomeCompleted = deleteVendors.every((vendor) => {
      const outcome = outcomes?.find((o) => o.vendor.id == vendor.vendorId);
      let result =
        (outcome?.outcome != null && outcome?.outcome !== "AUTOMATION_FAILED") ||
        vendor.isInstalled === true;
      return result;
    });

    setStepCompleted(
      Boolean(deleteVendors) && deleteVendors.length <= outcomes.length && everyOutcomeCompleted,
    );
  }, [setStepCompleted, deleteVendors, request.vendorOutcomes, vendors]);

  const vendorOutcomes = useMemo(() => {
    const outcomes = request.vendorOutcomes.reduce(
      (acc, outcome) => Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome }),
      {},
    );

    request._local?.pendingVendorOutcomeUpdates?.forEach((pending) => {
      outcomes[pending.vendorId] = pending.outcome;
    });

    return outcomes;
  }, [request.vendorOutcomes, request._local?.pendingVendorOutcomeUpdates]);

  // The Connected System row has no manual controls, so the step waits on its automation (POL-7958).
  const stepAwaitsConnectedSystem = deleteVendors.some((vendor) =>
    isAwaitingConnectedSystemAutomation(vendor, vendorOutcomes[vendor.vendorId], request),
  );
  useConnectedSystemAutomationBlock(stepAwaitsConnectedSystem, request.allowManualAutomation);

  const pendingUpdates = useMemo(() => {
    const pending = request._local?.pendingVendorOutcomeUpdates?.map((pending) => pending.vendorId);
    return new Set(pending);
  }, [request._local?.pendingVendorOutcomeUpdates]);

  // Automation outcomes recorded on the request whose vendor is not shown as an outcome row above
  // (e.g. a vendor that has been filtered out of the delete list). These are surfaced in a separate
  // "Automations" section so the outcome isn't silently dropped.
  const automationOnlyOutcomes = useMemo(() => {
    const shownVendorIds = new Set(deleteVendors.map((v) => v.vendorId));
    return request.vendorOutcomes.filter(
      (outcome) =>
        AUTOMATION_OUTCOMES.includes(outcome.outcome) && !shownVendorIds.has(outcome.vendor.id),
    );
  }, [request.vendorOutcomes, deleteVendors]);

  if (!readyToRender) return null;

  return (
    <>
      <div className="text-t3 mb-std">
        Select how you’ve processed data handled by each Data Recipient. Your processing defaults
        are marked; select them only if they apply for this request. Your defaults and instructions
        can be updated in{" "}
        <Link className="text-weight-regular" to="/organization/requestHandling">
          Request Instructions.
        </Link>
      </div>

      {deleteVendors && (
        <CategorizedVendorList
          vendors={deleteVendors}
          renderVendor={(vendor) => {
            // Check if this vendor is a management vendor
            const isManagementVendor = managementVendors.some((v) => v.id === vendor.id);
            const customAudienceManagementVendor = isManagementVendor ? vendor : undefined;

            // Get only the vendors nested under THIS specific vendor
            const networksManaged = customAudienceManagementVendor
              ? getManagedVendorsForManager(vendor.id)
              : undefined;

            return (
              <VendorDeleteRow
                key={vendor.id}
                vendor={vendor}
                authorizations={authorizations}
                vendorOutcomes={vendorOutcomes}
                updatePending={pendingUpdates.has(vendor.vendorId)}
                onSelectionChange={onSelectionChange}
                org={org}
                request={request}
                managedCustomAudienceVendors={networksManaged}
                customAudienceManagementVendor={customAudienceManagementVendor}
                instructionsState={instructionsState}
              />
            );
          }}
        />
      )}

      {automationOnlyOutcomes.length > 0 && (
        <>
          <CategoryHeader categoryName="Automations" showVendorsSuffix={false} />

          {automationOnlyOutcomes.map((outcome) => (
            <AutomationOutcomeRow
              key={outcome.vendor.id}
              vendor={outcome.vendor}
              outcome={outcome.outcome}
              variant="delete"
              nestedVendors={getManagedVendorsForManager(
                allVendors?.find((v) => v.vendorId === outcome.vendor.id)?.id,
              )}
            />
          ))}
        </>
      )}

      {retainVendors && retainVendors.length > 0 && (
        <>
          <div className="text-t5 text-weight-medium mb-md"> Data Retention </div>

          <div className="text-t3 mb-mdlg">
            The systems below are marked for retention based on one or more{" "}
            <Abbr title="Your business may retain personal data under certain exceptions, such as for internal use and to comply with a legal obligation.">
              <strong>exceptions to deletion</strong>
            </Abbr>
            . Do not delete data stored with these Data Recipients if it is kept under an exception
            to deletion. Data retention settings can be updated in your{" "}
            <Link to={Routes.workflows.dataStorageSettings}>Data Storage settings</Link>.
          </div>

          <CategorizedVendorList
            vendors={retainVendors}
            renderVendor={(vendor) => <RetainedVendorRow key={vendor.id} vendor={vendor} />}
          />
        </>
      )}
    </>
  );
};
